import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { CreditCard, CalendarDays, Wallet, Users, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PaymentsTab } from "@/components/admin/payments-tab";
import { BookingsTab } from "@/components/admin/bookings-tab";
import { PayoutsTab } from "@/components/admin/payouts-tab";
import { UsersTab } from "@/components/admin/users-tab";

export const Route = createFileRoute("/_authenticated/admin")({
  beforeLoad: async () => {
    // Lazy import: beforeLoad lives in the initial bundle, the auth SDK should not.
    const { supabase } = await import("@/integrations/supabase/client");
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) throw redirect({ to: "/auth" });
    const { data: isAdmin } = await supabase.rpc("has_role", {
      _user_id: auth.user.id,
      _role: "admin",
    });
    if (!isAdmin) throw redirect({ to: "/dashboard" });
  },
  head: () => ({
    meta: [
      { title: "Admin Portal | Smart Word Labs" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content: "Internal Smart Word Labs admin portal for payments, bookings, payouts and users.",
      },
      { property: "og:title", content: "Admin Portal | Smart Word Labs" },
      { property: "og:description", content: "Internal administration portal." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const { t } = useTranslation("portal");

  const TABS = [
    { value: "payments", label: t("admin.tabs.payments"), icon: CreditCard },
    { value: "bookings", label: t("admin.tabs.bookings"), icon: CalendarDays },
    { value: "payouts", label: t("admin.tabs.payouts"), icon: Wallet },
    { value: "users", label: t("admin.tabs.users"), icon: Users },
  ];

  return (
    <main className="mx-auto max-w-7xl px-5 py-10">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ms-2">
        <Link to="/dashboard">
          <ArrowLeft className="me-1 h-4 w-4" /> {t("admin.backToDashboard")}
        </Link>
      </Button>

      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">{t("admin.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("admin.description")}</p>
      </header>

      <Tabs defaultValue="payments" className="gap-6 lg:flex lg:items-start">
        <TabsList className="mb-6 h-auto w-full flex-wrap justify-start gap-1 lg:mb-0 lg:w-56 lg:flex-col lg:items-stretch lg:bg-transparent lg:p-0">
          {TABS.map(({ value, label, icon: Icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="justify-start gap-2 lg:w-full lg:border lg:border-transparent lg:px-3 lg:py-2 lg:data-[state=active]:border-border"
            >
              <Icon className="h-4 w-4" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="min-w-0 flex-1">
          <TabsContent value="payments">
            <PaymentsTab />
          </TabsContent>
          <TabsContent value="bookings">
            <BookingsTab />
          </TabsContent>
          <TabsContent value="payouts">
            <PayoutsTab />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
        </div>
      </Tabs>
    </main>
  );
}
